import {
  CognitoIdentityCredentialProvider as Credentials,
  fromCognitoIdentityPool,
} from '@aws-sdk/credential-providers';

import { Auth } from '@aws-amplify/auth';
import { Hub, Logger as AmplifyLogger } from '@aws-amplify/core';
import { getVisitorId } from '../analytics/fingerprint';
import { isBrandAppV2 } from '../api/cache/appConfigQueryOptions';
import BrandclubStoreConfigUtils from '../utils/brandclubStoreConfigUtils';
import { Logger } from '../utils/logger';
import { getAccessToken } from './accessToken';
import CrossDomainStorage, { type SessionStore } from './CrossDomainStorage';

export const AmplifyAuth: typeof Auth = Auth;

export const AmplifyHub: typeof Hub = Hub;

export { getAccessToken, getAuthorizationHeader } from './accessToken';
// Export CrossDomainStorage
export { default as CrossDomainStorage } from './CrossDomainStorage';
interface CognitoUserAttributes {
  sub: string;
  birthdate?: string;
  email_verified?: boolean;
  gender?: string;
  name?: string;
  phone_number_verified?: boolean;
  phone_number?: string;
  given_name?: string;
  family_name?: string;
  email: string;
  picture?: string;
}

export interface IAppAuthConfig {
  userPoolId: string;
  userPoolClientId: string;
  identityPoolId: string;
  signInDomain: string;
  region: string;
}

export interface CognitoUserInfo {
  id: string;
  username: string;
  attributes: CognitoUserAttributes;
}
AmplifyLogger.LOG_LEVEL = 'NONE';

const logger = new Logger('Auth', BrandclubStoreConfigUtils.getAppStage());

// Add this interface for login code redemption
interface LoginCodeRedemptionResponse {
  status: string;
  data?: {
    accessToken: string;
    accessTokenExpiresIn: number;
    refreshToken: string;
    refreshTokenExpiresIn: number;
    idToken: string;
    idTokenExpiresIn: number;
  };
}

const authConfiguredPromise = new Promise<void>((resolve) => {
  const unsubscribe = AmplifyHub.listen('auth', (data: { payload: { event: string } }) => {
    if (data?.payload?.event === 'configured') {
      unsubscribe();
      resolve();
    }
  });
});

export const waitForAuthConfigured = async (timeout = 5000) => {
  const timeoutPromise = new Promise((_, reject) => {
    logger.debug(`Waiting for auth to be configured with timeout of ${timeout}ms`);
    setTimeout(() => {
      reject(new Error('Auth not configured after timeout'));
    }, timeout);
  });
  return Promise.race([authConfiguredPromise, timeoutPromise]);
};

const getAWSCognitoConfig = async (
  authConfig: IAppAuthConfig,
  domainName: string | undefined,
  sessionStore: SessionStore,
) => {
  try {
    const appStage = BrandclubStoreConfigUtils.getShopperIdentityStage();
    const authDomain = BrandclubStoreConfigUtils.getLocalStorageDomain(appStage, {
      domainName,
    });
    // update current hostname to auth domain
    const authOrigin = `https://${authDomain}`;
    if (domainName) {
      logger.debug(`Using auth origin from provided domainName: ${domainName}`);
    } else {
      logger.warn(`Using fallback localStorageDomain: ${authOrigin} as domainName is not provided`);
    }
    const storage = CrossDomainStorage.createInstance(authOrigin, appStage, { sessionStore });
    await storage.initialize();
    let cognitoCredentials: Credentials | undefined = undefined;
    return {
      region: authConfig.region,
      userPoolId: authConfig.userPoolId,
      userPoolWebClientId: authConfig.userPoolClientId,
      oauth: {
        domain: authConfig.signInDomain,
        scope: ['phone', 'email', 'profile', 'openid', 'aws.cognito.signin.user.admin'],
        redirectSignIn: `${origin}/signinredirect`,
        redirectSignOut: `${origin}/signoutredirect`,
        responseType: 'code',
      },
      storage: storage,
      Auth: {
        domain: authDomain,
        userPoolId: authConfig.userPoolId,
        clientId: authConfig.userPoolClientId,
        identityPoolId: authConfig.identityPoolId,
        userPoolWebClientId: authConfig.userPoolClientId,
        ClientId: authConfig.userPoolClientId,
        region: authConfig.region,
        credentials: (() => {
          if (!authConfig.identityPoolId) {
            return undefined;
          }
          if (!cognitoCredentials) {
            cognitoCredentials = fromCognitoIdentityPool({
              identityPoolId: authConfig.identityPoolId,
              clientConfig: { region: authConfig.region },
            });
          }
          return cognitoCredentials;
        })(),
      },
    };
  } catch (err) {
    logger.error('Error getting AWS Cognito config', err);
    throw err;
  }
};

// Add function to redeem login code
const redeemBrandAppLoginCode = async ({
  apiDomain,
  loginCode,
  signal,
}: {
  apiDomain?: string;
  loginCode: string;
  signal?: AbortSignal;
}): Promise<LoginCodeRedemptionResponse> => {
  // Resolve the api-rewards base for the redeem call.
  //   back-room (Plan 4 / D-18): MUST go same-origin through the App-Proxy
  //     forwarder (`${origin}${routePath}/api-rewards`) so the redeem reaches the
  //     SAME backend (and Redis) that minted the code via `proxy.shopify-login`,
  //     and so it doesn't escape the proxy subpath (bare `/api-rewards` → Shopify
  //     404) or go cross-origin (CORS). `getApiHostName()` already includes
  //     `${routePath}/api-rewards`.
  //   front-room: unchanged — explicit `apiDomain` (cross-domain SSO) wins;
  //     otherwise fall back to the same-origin host.
  const routePath = BrandclubStoreConfigUtils.getRoutePath?.() ?? '';
  let baseUrl: string;
  if (routePath) {
    baseUrl = BrandclubStoreConfigUtils.getApiHostName();
  } else if (apiDomain) {
    baseUrl = `https://${apiDomain}/api-rewards`;
  } else {
    baseUrl = BrandclubStoreConfigUtils.getApiHostName();
  }
  const res = await fetch(`${baseUrl}/brandapp/redeemBrandAppLoginCode`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ loginCode }),
    signal,
  });

  if (res.ok) {
    return res.json();
  } else {
    throw new Error('Error redeeming brand app login code');
  }
};

/**
 * Stash redeemed login-code tokens in the temporary pending-auth slot.
 * We need to store tokens in the format Amplify Auth expects
 * (CognitoIdentityServiceProvider.{clientId}.{username}.{tokenType}), but the
 * client id may not be known yet at redeem time — so this stashes them and
 * `applyPendingAuthTokens` writes the real keys once the client id is known.
 */
const stashPendingAuthTokens = (data: LoginCodeRedemptionResponse['data']): boolean => {
  if (!data?.accessToken || !data?.refreshToken) {
    return false;
  }
  // Parse the access token to get user info
  const tokenParts = data.accessToken.split('.');
  if (tokenParts.length !== 3) {
    return false;
  }
  try {
    const payload = JSON.parse(atob(tokenParts[1]));
    const username = payload.username || payload['cognito:username'] || payload.sub;

    const appStage = BrandclubStoreConfigUtils.getShopperIdentityStage();
    const storage = CrossDomainStorage.getCurrentInstance();

    if (!storage) {
      logger.error('CrossDomainStorage not initialized when trying to store login tokens');
      return false;
    }

    if (!username) {
      return false;
    }
    const tempAuthData = {
      accessToken: data.accessToken,
      accessTokenExpiresIn: data.accessTokenExpiresIn,
      refreshToken: data.refreshToken,
      refreshTokenExpiresIn: data.refreshTokenExpiresIn,
      idToken: data.idToken,
      idTokenExpiresIn: data.idTokenExpiresIn,
      username: username,
      timestamp: Date.now(),
    };

    storage.setItem(`bc-pending-auth-${appStage}`, JSON.stringify(tempAuthData));
    return true;
  } catch (error) {
    logger.error('Error parsing access token:', error);
    return false;
  }
};

// Add function to check and handle login code
const checkAndHandleLoginCode = async (apiDomain?: string): Promise<boolean> => {
  const url = new URL(window.location.href);
  const loginCode = url.searchParams.get('bcLoginCode');
  const shouldLogOut = url.searchParams.get('bcLogout') === '1';
  if (!loginCode && !shouldLogOut) {
    return false;
  }
  // delete from the url without reloading the page
  url.searchParams.delete('bcLoginCode');
  url.searchParams.delete('bcLogout');
  window.history.replaceState({}, document.title, url.toString());

  try {
    if (loginCode && !shouldLogOut) {
      logger.debug(`Found login code in URL, attempting to redeem ${loginCode} from ${apiDomain}`);
      const res = await redeemBrandAppLoginCode({ apiDomain, loginCode });
      if (res.status === 'success' && stashPendingAuthTokens(res.data)) {
        logger.debug('Login code redeemed successfully');
        return true;
      }
    } else if (shouldLogOut) {
      try {
        logger.debug('Found bcLogout in URL, attempting to log out');
        await signOut();
        return true;
      } catch (error) {
        logger.error('Error signing out:', error);
        return true;
      } finally {
        url.searchParams.delete('bcLogout');
        window.history.replaceState({}, document.title, url.toString());
      }
    }
  } catch (error) {
    logger.error('Error redeeming login code:', error);
  }
  return false;
};

/**
 * Write stashed pending-auth tokens (see `stashPendingAuthTokens`) into
 * Amplify's storage-key format for the given user-pool client id, and dispatch
 * the signIn Hub event. Returns true when tokens were applied.
 */
const applyPendingAuthTokens = (clientId: string): boolean => {
  const appStage = BrandclubStoreConfigUtils.getShopperIdentityStage();
  const storage = CrossDomainStorage.getCurrentInstance();
  if (!storage) {
    return false;
  }
  const pendingAuthKey = `bc-pending-auth-${appStage}`;
  const pendingAuthData = storage.getItem(pendingAuthKey);
  if (!pendingAuthData) {
    return false;
  }
  try {
    const authData = JSON.parse(pendingAuthData);
    const username = authData.username;

    // Store tokens in Amplify's expected format
    // Format: CognitoIdentityServiceProvider.{clientId}.{username}.{tokenType}
    const keyPrefix = `CognitoIdentityServiceProvider.${clientId}.${username}`;

    // Store access token
    storage.setItem(`${keyPrefix}.accessToken`, authData.accessToken);

    // Store refresh token
    storage.setItem(`${keyPrefix}.refreshToken`, authData.refreshToken);

    // Store ID token
    storage.setItem(`${keyPrefix}.idToken`, authData.idToken);

    // Store the last authenticated user
    storage.setItem(`CognitoIdentityServiceProvider.${clientId}.LastAuthUser`, username);

    // Calculate and store token expiration time
    const clockDrift = 0; // You might want to calculate this based on server time

    storage.setItem(`${keyPrefix}.clockDrift`, clockDrift.toString());

    // Store user attributes (decode from access token)
    const tokenParts = authData.accessToken.split('.');
    if (tokenParts.length === 3) {
      const payload = JSON.parse(atob(tokenParts[1]));
      const userData = {
        UserAttributes: [{ Name: 'sub', Value: payload.sub || username }],
        Username: username,
      };
      for (const key in payload) {
        if (key !== 'sub' && key !== 'cognito:username') {
          userData.UserAttributes.push({ Name: key, Value: payload[key] });
        }
      }
      storage.setItem(`${keyPrefix}.userData`, JSON.stringify(userData));
    }

    // Remove the pending auth data
    storage.removeItem(pendingAuthKey);

    logger.debug('Successfully processed pending auth tokens');

    // Dispatch auth event to notify the app that user is signed in
    Hub.dispatch('auth', {
      event: 'signIn',
      data: { username: username },
    });

    return true;
  } catch (error) {
    logger.error('Error processing pending auth tokens:', error);
    storage.removeItem(pendingAuthKey);
    return false;
  }
};

// The user-pool client id Auth was configured with — needed by
// redeemAndApplyLoginCode to write Amplify storage keys after init.
let configuredUserPoolClientId: string | null = null;

const initializeAuth = async (authConfig: IAppAuthConfig, domainName?: string) => {
  // A V2 app signs the shopper in on the page's own door, so the session lives in this page's storage.
  // A legacy app keeps the bridge.
  const sessionStore: SessionStore = (await isBrandAppV2()) ? 'origin' : 'bridge';
  // This also loads the session storage that Amplify reads.
  const cognitoConfig = await getAWSCognitoConfig(authConfig, domainName, sessionStore);
  Auth.configure(cognitoConfig);
  configuredUserPoolClientId = authConfig.userPoolClientId;
  const hasLoginCode = await checkAndHandleLoginCode(domainName);
  const applied = applyPendingAuthTokens(authConfig.userPoolClientId);

  // If we processed pending auth from a login code, reload to ensure Auth
  // picks up the tokens
  if (applied && hasLoginCode) {
    window.location.reload();
  }

  return cognitoConfig;
};

/**
 * Redeem a bcLoginCode and apply the tokens IN PLACE — no `?bcLoginCode=…`
 * navigation round-trip (saves a full page load in the Shopify auto-SSO flow,
 * Storefront Concierge Plan 4/5). Requires auth to already be initialized
 * (`initializeAmplifyAuth` completed: storage bridge + client id available).
 * Returns true when tokens were applied; the caller refreshes app state
 * (typically one window.location.reload()).
 */
export const redeemAndApplyLoginCode = async ({
  loginCode,
  apiDomain,
}: {
  loginCode: string;
  apiDomain?: string;
}): Promise<boolean> => {
  if (!configuredUserPoolClientId) {
    logger.error('redeemAndApplyLoginCode called before auth was configured');
    return false;
  }
  try {
    const res = await redeemBrandAppLoginCode({ apiDomain, loginCode });
    if (res.status !== 'success' || !stashPendingAuthTokens(res.data)) {
      return false;
    }
    return applyPendingAuthTokens(configuredUserPoolClientId);
  } catch (error) {
    logger.error('Error redeeming login code:', error);
    return false;
  }
};

let amplifyAuthPromise: Promise<void> | null = null;
export const initializeAmplifyAuth = async (authConfig: IAppAuthConfig, domainName?: string) => {
  if (!amplifyAuthPromise) {
    amplifyAuthPromise = (async () => {
      try {
        await initializeAuth(authConfig, domainName);
      } catch (err) {
        logger.error('Error initializing Amplify Auth', err);
        // reset the promise to allow for a new attempt
        amplifyAuthPromise = null;
      }
    })();
  }
  return amplifyAuthPromise;
};

export const getIdToken = async () => {
  const session = await Auth.currentSession().catch((_e) => {
    return null;
  });
  if (!session) {
    return null;
  }
  const idToken = session.getIdToken();
  return idToken;
};

export const getRefreshToken = async () => {
  const session = await Auth.currentSession().catch((_e) => {
    return null;
  });
  if (!session) {
    return null;
  }
  return session?.getRefreshToken();
};

type GetUserSignedInStateResponse =
  | {
      signedIn: true;
      userInfo: CognitoUserInfo;
    }
  | {
      signedIn: false;
      userInfo: null;
    };

// Promise cache for deduplicating concurrent calls
let pendingUserSignedInStatePromise: Promise<GetUserSignedInStateResponse> | null = null;

const createAuthResponse = (userInfo: CognitoUserInfo | undefined): GetUserSignedInStateResponse => {
  if (!userInfo || !userInfo.username) {
    return { signedIn: false, userInfo: null };
  }
  return { signedIn: true, userInfo };
};

export const getUserSignedInState = async (): Promise<GetUserSignedInStateResponse> => {
  // Return existing promise if one is pending
  if (pendingUserSignedInStatePromise) {
    return pendingUserSignedInStatePromise;
  }
  // Create new promise and cache it
  pendingUserSignedInStatePromise = (async () => {
    try {
      const session = await Auth.currentSession();
      if (!session || !session.isValid()) {
        return createAuthResponse(undefined);
      }
      const userInfo: CognitoUserInfo | undefined = await Auth.currentUserInfo();
      if (!userInfo || !userInfo.username) {
        return createAuthResponse(undefined);
      }
      return createAuthResponse(userInfo);
    } catch (e) {
      return createAuthResponse(undefined);
    } finally {
      pendingUserSignedInStatePromise = null;
    }
  })();

  return pendingUserSignedInStatePromise;
};

/**
 * Remove the locally cached Cognito session (the same keys
 * `applyPendingAuthTokens` writes) WITHOUT calling Cognito. Used when the
 * stored tokens belong to a DIFFERENT client than the web app's (backend-minted
 * bcLoginCode sessions — Q11), where a network revoke can never succeed.
 * Returns true when a cached session was found and cleared.
 */
const clearLocalCognitoSession = (): boolean => {
  const storage = CrossDomainStorage.getCurrentInstance();
  const clientId = configuredUserPoolClientId;
  if (!storage || !clientId) {
    return false;
  }
  const lastAuthUserKey = `CognitoIdentityServiceProvider.${clientId}.LastAuthUser`;
  const username = storage.getItem(lastAuthUserKey);
  if (!username) {
    return false;
  }
  const keyPrefix = `CognitoIdentityServiceProvider.${clientId}.${username}`;
  for (const suffix of ['accessToken', 'refreshToken', 'idToken', 'clockDrift', 'userData']) {
    storage.removeItem(`${keyPrefix}.${suffix}`);
  }
  storage.removeItem(lastAuthUserKey);
  return true;
};

export const signOut = async () => {
  try {
    await waitForAuthConfigured();
    // Backend-minted sessions (bcLoginCode / Shopify login) are issued on a
    // DIFFERENT Cognito client than the web app's (open question Q11). For
    // those, Amplify's sign-out RevokeToken call is guaranteed to fail with
    // "Invalid client or secret" — and the Cognito SDK wraps it in a jittered
    // exponential retry, so the user watches ~6 failed 400s (~5 s) before the
    // local session finally clears. Detect the mismatch from the access
    // token's client_id claim and sign out LOCALLY instead: clearing the
    // cached session is the only effective part here anyway (the foreign
    // refresh token cannot be revoked with our client id and simply expires
    // server-side).
    try {
      const accessToken = await getAccessToken();
      const tokenClientId = accessToken?.decodePayload?.()?.client_id;
      if (
        tokenClientId &&
        configuredUserPoolClientId &&
        tokenClientId !== configuredUserPoolClientId &&
        clearLocalCognitoSession()
      ) {
        logger.debug('Signed out locally (foreign-client token; network revoke skipped)');
        AmplifyHub.dispatch('auth', { event: 'signOut' });
        return;
      }
    } catch (e) {
      logger.debug('Foreign-client token check failed; falling back to Auth.signOut()', e);
    }
    await Auth.signOut();
    const jwt = await getJwt();
    if (jwt) {
      logger.error('Failed to sign out, JWT still exists');
    } else {
      logger.debug('Signed out successfully dispatching signOut event and clearing query cache');
      AmplifyHub.dispatch('auth', { event: 'signOut' });
    }
  } catch (e) {
    logger.error('Error signing out', e);
  }
};

export const getJwt = async () => {
  try {
    const accessToken = await getAccessToken();
    return accessToken?.getJwtToken() || '';
  } catch (e) {
    if (e !== 'No current user') {
      logger.error('Error getting JWT', e);
    }
    return '';
  }
};

export const getVisitorIdHeader = async () => {
  const uniqueVisitorIdToUse = await getVisitorId();
  const headers: Record<string, string> = {};
  if (uniqueVisitorIdToUse) {
    headers['x-bc-visitor-id'] = uniqueVisitorIdToUse;
  }
  return headers;
};

export const getHeadersWithUniqueVisitorId = async () => {
  const uniqueVisitorIdToUse = await getVisitorId();
  const jwt = await getJwt();
  const headers: any = {
    'Content-Type': 'application/json',
  };
  if (jwt) {
    headers['Authorization'] = `Bearer ${jwt}`;
  }
  if (uniqueVisitorIdToUse) {
    headers['x-bc-visitor-id'] = uniqueVisitorIdToUse;
  }
  return headers;
};

export const setOtpAuth = async (userInfo: any) => {
  return dispatchSignInComplete(userInfo);
};

export const dispatchSignInComplete = (userInfo: any) => {
  userInfo = {
    ...userInfo,
    ...(userInfo.cognitoUserAttributes || {}),
    username: userInfo.username || userInfo.userId,
  };
  Hub.dispatch('auth', {
    event: 'signIn_Complete',
    data: { username: userInfo.username },
  });
};

export { clearShopifyLoginAttempt, getConciergeSignInUrl, shopifyLoginUrl, SHOPIFY_LOGIN_ATTEMPT_KEY } from './shopifyLogin';
export { currentReturnPath, RETURN_TO_PARAM, safeReturnPath } from './returnPath';
export {
  memberOrderUrl,
  memberProfileUrl,
  memberPurchasesUrl,
  openMemberLink,
  type MemberLinkAuthType,
  type PurchasesPage,
} from './memberLinks';
